import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import jsPDF from "jspdf";
import {
  FileText, Download, Sparkles, Check, Upload,
  Loader2, AlertCircle, X, User, Briefcase, GraduationCap, Wrench, Mic, MicOff
} from 'lucide-react';
import APIService from '../services/api';

const supportedUploadTypes = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

const isSupportedUploadFile = (file) => {
  const name = (file?.name || '').toLowerCase();
  return supportedUploadTypes.includes(file?.type) || name.endsWith('.pdf') || name.endsWith('.docx') || name.endsWith('.txt');
};

const ROLE_GROUPS = [
  { label: 'Business & finance', roles: ['Accountant', 'Business Analyst', 'Business Development Manager', 'Consultant', 'Data Analyst', 'Financial Analyst', 'Investment Banker', 'Marketing Manager', 'Operations Manager', 'Product Manager', 'Project Manager', 'Sales Manager'] },
  { label: 'Technology', roles: ['AI / Machine Learning Engineer', 'Cloud Engineer', 'Cybersecurity Analyst', 'Data Engineer', 'Data Scientist', 'DevOps Engineer', 'Frontend Developer', 'Full Stack Developer', 'IT Support Specialist', 'Mobile Developer', 'Product Designer (UX/UI)', 'QA Engineer', 'Software Engineer', 'Solutions Architect'] },
  { label: 'People, legal & education', roles: ['Attorney / Legal Counsel', 'Customer Success Manager', 'HR Manager', 'Recruiter', 'Teacher / Lecturer', 'Training and Development Specialist'] },
  { label: 'Healthcare & science', roles: ['Clinical Research Associate', 'Doctor', 'Laboratory Technician', 'Nurse', 'Pharmacist', 'Research Scientist'] },
  { label: 'Engineering & skilled professions', roles: ['Architect', 'Civil Engineer', 'Electrical Engineer', 'Mechanical Engineer', 'Supply Chain Manager', 'UX Researcher'] },
];

const CUSTOM_ROLE_VALUE = 'custom';

const VoiceInputButton = ({ value, onTranscript, fieldName, className = '' }) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const startingValueRef = useRef('');

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      window.alert('Voice input is not available in this browser. Please use the latest Chrome or Edge browser, allow microphone access, or type your response.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';
    startingValueRef.current = value?.trim() ? `${value.trim()} ` : '';
    recognition.onresult = (event) => {
      let transcript = '';
      for (let index = 0; index < event.results.length; index += 1) transcript += event.results[index][0].transcript;
      onTranscript(`${startingValueRef.current}${transcript}`.trim());
    };
    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        window.alert('Microphone access was blocked. Allow it in your browser settings and try again.');
      }
    };
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  return (
    <Button type="button" variant={isListening ? 'destructive' : 'outline'} size="sm" onClick={toggleListening} className={`shrink-0 ${className}`} title={isListening ? `Stop recording ${fieldName}` : `Use voice to fill ${fieldName}`}>
      {isListening ? <MicOff className="w-4 h-4 mr-1" /> : <Mic className="w-4 h-4 mr-1" />}
      {isListening ? 'Stop' : 'Speak'}
    </Button>
  );
};

const TEMPLATE_DETAILS = {
  modern: { name: 'Modern', description: 'Clean accent bar', color: [37, 99, 235] },
  classic: { name: 'Classic', description: 'Timeless serif layout', color: [55, 65, 81] },
  executive: { name: 'Executive', description: 'Bold leadership style', color: [15, 118, 110] },
  minimal: { name: 'Minimal', description: 'Simple and focused', color: [23, 23, 23] },
};

const ResumeTemplatePreview = ({ template, formData, role, compact = false }) => {
  const primary = template === 'executive' ? 'text-teal-800' : template === 'classic' ? 'text-slate-800' : template === 'minimal' ? 'text-neutral-900' : 'text-blue-700';
  const border = template === 'executive' ? 'border-teal-700' : template === 'classic' ? 'border-slate-700' : template === 'minimal' ? 'border-neutral-900' : 'border-blue-600';
  const name = formData.fullName || 'Your Name';
  const heading = formData.title || role || 'Professional Title';
  const sample = formData.summary || 'A concise professional summary appears here as you build your resume.';

  return (
    <div className={`bg-white text-gray-800 shadow-sm ${compact ? 'min-h-28 p-2 text-[6px]' : 'min-h-[430px] p-5 text-xs'} ${template === 'classic' ? 'font-serif' : 'font-sans'}`}>
      {template === 'modern' && <div className="h-2 bg-blue-600 -mx-5 -mt-5 mb-4" />}
      <div className={template === 'executive' ? 'border-l-4 border-teal-700 pl-3' : template === 'minimal' ? 'border-b border-neutral-900 pb-2' : ''}>
        <p className={`font-bold ${primary} ${compact ? 'text-[9px]' : 'text-xl'}`}>{name}</p>
        <p className={`uppercase tracking-wide ${compact ? 'text-[5px]' : 'text-[10px]'} text-gray-500`}>{heading}</p>
        {!compact && <p className="mt-1 text-[10px] text-gray-500">{[formData.email, formData.phone, formData.location].filter(Boolean).join(' · ') || 'email@example.com · City, Country'}</p>}
      </div>
      <div className={compact ? 'mt-2 space-y-1' : 'mt-5 space-y-4'}>
        {[
          ['Profile', sample],
          ['Experience', formData.experience || 'Your relevant achievements and responsibilities'],
          ['Education', formData.education || 'Your degree, institution, and certifications'],
          ['Skills', formData.skills || 'Role-relevant skills'],
        ].map(([label, content]) => (
          <div key={label}>
            <p className={`font-bold uppercase tracking-wider ${primary} border-b ${border} ${compact ? 'text-[5px]' : 'text-[10px] pb-1'}`}>{label}</p>
            <p className={`mt-1 whitespace-pre-line text-gray-600 ${compact ? 'line-clamp-2' : 'line-clamp-4 leading-relaxed'}`}>{content}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const TargetRoleField = ({ id, selectedRole, customRole, onSelectedRoleChange, onCustomRoleChange }) => (
  <div className="space-y-3">
    <div>
      <Label htmlFor={id} className="text-sm font-semibold text-gray-700 mb-1 block">
        Target Role <span className="text-red-500">*</span>
      </Label>
      <Select value={selectedRole} onValueChange={onSelectedRoleChange}>
        <SelectTrigger id={id} className="h-11 w-full"><SelectValue placeholder="Choose your target role" /></SelectTrigger>
        <SelectContent>
          {ROLE_GROUPS.map((group) => (
            <SelectGroup key={group.label}>
              <SelectLabel>{group.label}</SelectLabel>
              {group.roles.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}
            </SelectGroup>
          ))}
          <SelectGroup>
            <SelectLabel>Other</SelectLabel>
            <SelectItem value={CUSTOM_ROLE_VALUE}>Custom / Any Other Role</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
    {selectedRole === CUSTOM_ROLE_VALUE && (
      <div>
        <Label htmlFor={`${id}-custom`} className="text-sm font-semibold text-gray-700 mb-1 block">Enter your target role</Label>
        <Input id={`${id}-custom`} value={customRole} onChange={(event) => onCustomRoleChange(event.target.value)} placeholder="e.g. Sustainability Consultant" className="h-11" autoFocus />
      </div>
    )}
  </div>
);


// ─── Upload Tab ────────────────────────────────────────────────────────────────
const UploadTab = ({ onAnalysisComplete }) => {
  const [file, setFile] = useState(null);
  const [targetRole, setTargetRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    if (!isSupportedUploadFile(f)) {
      setError('Please upload a PDF, DOCX, or TXT file.');
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleAnalyze = async () => {
    const effectiveTargetRole = targetRole === CUSTOM_ROLE_VALUE ? customRole.trim() : targetRole;
    if (!file) { setError('Please upload a resume file.'); return; }
    if (!effectiveTargetRole) { setError('Please choose a target role or enter a custom role.'); return; }
    setLoading(true);
    setError(null);
    try {
      const { text } = await APIService.extractResumeText(file);
      const result = await APIService.analyzeATS(text, effectiveTargetRole);
      onAnalysisComplete(result, text, effectiveTargetRole);
    } catch (err) {
      setError(err.message || 'Failed to analyze resume. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current.click()}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-200
          ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.txt"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {file ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-800">{file.name}</p>
              <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setFile(null); }}
              className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700"
            >
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-700">Drop your resume here</p>
              <p className="text-sm text-gray-500 mt-1">or click to browse — PDF, DOCX, or TXT</p>
            </div>
          </div>
        )}
      </div>

      {/* Target Role */}
      <div>
        <TargetRoleField id="upload-target-role" selectedRole={targetRole} customRole={customRole} onSelectedRoleChange={setTargetRole} onCustomRoleChange={setCustomRole} />
        <p className="text-xs text-gray-500 mt-1">We'll tailor the ATS analysis to this role</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <Button
        onClick={handleAnalyze}
        disabled={loading || !file}
        className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700"
      >
        {loading ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing with AI...</>
        ) : (
          <><Sparkles className="w-5 h-5 mr-2" /> Analyze My Resume</>
        )}
      </Button>
    </div>
  );
};


// ─── ATS Results Panel ─────────────────────────────────────────────────────────
// FIX #1: All hooks moved to top of component (were previously mid-JSX or outside component)
// FIX #2: rawCVText accepted as prop (was incorrectly referencing outer-scope variable)
// FIX #6: Enhance CV buttons moved inside this component's return (were floating outside)
const ATSResults = ({ results, rawCVText, jdText = '', targetRole, onReplaceWorkingCV }) => {
  const [enhancing, setEnhancing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [enhancedCV, setEnhancedCV] = useState(null);
  const [enhancementWarning, setEnhancementWarning] = useState(null);
  const [reanalysisMessage, setReanalysisMessage] = useState(null);
  const [updatedResults, setUpdatedResults] = useState(null);
  const [recommendationDecisions, setRecommendationDecisions] = useState({});

  useEffect(() => {
    setRecommendationDecisions({});
    setEnhancedCV(null);
    setEnhancementWarning(null);
    setReanalysisMessage(null);
    setUpdatedResults(null);
  }, [results]);

  if (!results) return null;

  const displayedResults = updatedResults || results;
  const originalScore = results.ats_score ?? results.score ?? 0;
  const score = displayedResults.ats_score ?? displayedResults.score ?? 0;
  const scoreColor = score >= 75 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600';
  const barColor = score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  const actionableRecommendations = [
    ...(results.recommendations || []).map((text, index) => ({ id: `recommendation-${index}`, text, type: 'Recommendation' })),
    ...(results.rewrite_suggestions || []).map((text, index) => ({ id: `rewrite-${index}`, text, type: 'Rewrite suggestion' })),
    ...(results.missing_cv_points || []).map((text, index) => ({ id: `gap-${index}`, text, type: 'JD gap to address' })),
  ];
  const approvedRecommendations = actionableRecommendations
    .filter((item) => recommendationDecisions[item.id] === 'approved')
    .map((item) => item.text);

  const setRecommendationDecision = (id, decision) => {
    setRecommendationDecisions((current) => ({ ...current, [id]: decision }));
    setEnhancedCV(null);
    setEnhancementWarning(null);
  };

  const handleEnhanceCV = async () => {
    if (!rawCVText) return alert("CV text missing");
    if (!approvedRecommendations.length) {
      alert('Approve at least one recommendation before incorporating changes into your CV.');
      return;
    }
    setEnhancing(true);
    try {
      const res = await APIService.enhanceCV({
        cvText: rawCVText,
        jdText,
        // Keywords are only added when an approved recommendation calls for them.
        // This prevents the AI from adding every missing keyword without consent.
        missingKeywords: [],
        recommendations: approvedRecommendations,
      });
      const enhancedText = res.enhanced_cv || res.enhancedCV;
      setEnhancedCV(enhancedText);
      setEnhancementWarning(res.warning || null);
      setReanalyzing(true);
      setReanalysisMessage(null);
      try {
        const refreshedAnalysis = await APIService.analyzeATS(enhancedText, targetRole || 'Target role');
        setUpdatedResults(refreshedAnalysis);
        const refreshedScore = refreshedAnalysis.ats_score ?? refreshedAnalysis.score ?? 0;
        const difference = refreshedScore - originalScore;
        setReanalysisMessage(difference > 0
          ? `ATS score updated: +${difference} points after incorporating your approved changes.`
          : difference < 0
            ? `ATS score was recalculated after incorporating your approved changes (${difference} points). Review the updated recommendations for the next improvement.`
            : 'ATS score was recalculated after incorporating your approved changes.');
      } catch (analysisError) {
        setReanalysisMessage('Your CV was updated, but its ATS score could not be recalculated just now. Please try again shortly.');
      } finally {
        setReanalyzing(false);
      }
    } catch (err) {
      alert(err.message || "Enhancement failed");
    } finally {
      setEnhancing(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!enhancedCV) return;
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 16;
    const lineHeight = 5.5;
    const lines = doc.splitTextToSize(enhancedCV, 178);
    let y = margin;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    lines.forEach((line) => {
      if (y + lineHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });
    doc.save("Enhanced_CV.pdf");
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          ATS Analysis Results
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* Overall Score */}
        <div className="text-center py-2">
          <div className={`text-5xl font-bold ${scoreColor}`}>{score}%</div>
          <p className="text-sm text-gray-600 mt-1">ATS Compatibility Score</p>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-3">
            <div
              className={`${barColor} h-2.5 rounded-full transition-all duration-700`}
              style={{ width: `${score}%` }}
            />
          </div>
          {reanalyzing && <p className="mt-3 text-xs font-medium text-blue-700">Recalculating your ATS score from the revised CV...</p>}
          {reanalysisMessage && <p className={`mt-3 rounded-md px-3 py-2 text-xs font-medium ${score > originalScore ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{reanalysisMessage}</p>}
        </div>

        {/* Section Scores */}
        {displayedResults.section_scores && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">📊 Section Breakdown</p>
            <div className="space-y-2">
              {Object.entries(displayedResults.section_scores).map(([section, pct]) => (
                <div key={section} className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 capitalize w-20">{section}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-8 text-right">{pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strengths */}
        {displayedResults.strengths?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">✅ Strengths</p>
            <ul className="space-y-2">
              {displayedResults.strengths.map((s, i) => (
                <li key={i} className="flex items-start text-sm text-gray-700 bg-green-50 rounded-lg px-3 py-2">
                  <Check className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Improvements */}
        {displayedResults.improvements?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">⚠️ Areas to Improve</p>
            <ul className="space-y-2">
              {displayedResults.improvements.map((s, i) => (
                <li key={i} className="flex items-start text-sm text-gray-700 bg-yellow-50 rounded-lg px-3 py-2">
                  <span className="mr-2 mt-0.5 flex-shrink-0">⚠️</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendation review */}
        {actionableRecommendations.length > 0 && (
          <div>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-gray-700">Review AI recommendations</p>
                <p className="text-xs text-gray-500 mt-1">Approve only the changes you want. Nothing is added to the CV until you incorporate approved changes.</p>
              </div>
              <Badge className="shrink-0 bg-green-100 text-green-800 text-xs">{approvedRecommendations.length} approved</Badge>
            </div>
            <ul className="space-y-2">
              {actionableRecommendations.map((item, i) => {
                const decision = recommendationDecisions[item.id];
                return (
                <li key={item.id} className={`rounded-lg border px-3 py-3 ${decision === 'approved' ? 'border-green-200 bg-green-50' : decision === 'rejected' ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-blue-100 bg-blue-50'}`}>
                  <div className="flex items-start text-sm text-gray-700">
                  <span className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center font-bold">
                    {i + 1}
                  </span>
                  <div className="flex-1">
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-blue-700 mb-1">{item.type}</span>
                    {item.text}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button size="sm" onClick={() => setRecommendationDecision(item.id, 'approved')} className={decision === 'approved' ? 'bg-green-700 hover:bg-green-700' : 'bg-green-600 hover:bg-green-700'}>
                        <Check className="w-3.5 h-3.5 mr-1" /> {decision === 'approved' ? 'Approved' : 'Approve & include'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setRecommendationDecision(item.id, 'rejected')} className="text-gray-600">
                        <X className="w-3.5 h-3.5 mr-1" /> {decision === 'rejected' ? 'Rejected' : 'Reject'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRecommendationDecision(item.id, 'ignored')} className="text-gray-500">
                        {decision === 'ignored' ? 'Ignored' : 'Ignore'}
                      </Button>
                    </div>
                  </div>
                  </div>
                </li>
              )})}
            </ul>
          </div>
        )}

        {/* Matched Keywords */}
        {displayedResults.matched_keywords?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">✅ Matched Keywords</p>
            <div className="flex flex-wrap gap-1.5">
              {displayedResults.matched_keywords.map((kw, i) => (
                <Badge key={i} className="bg-green-100 text-green-800 text-xs">{kw}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Missing Keywords */}
        {displayedResults.missing_keywords?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">⚠️ Missing Keywords</p>
            <div className="flex flex-wrap gap-1.5">
              {displayedResults.missing_keywords.map((kw, i) => (
                <Badge key={i} className="bg-red-100 text-red-800 text-xs">{kw}</Badge>
              ))}
            </div>
          </div>
        )}

        {displayedResults.missing_cv_points?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">CV Gaps Against JD</p>
            <ul className="space-y-2">
              {displayedResults.missing_cv_points.map((item, i) => (
                <li key={i} className="text-sm text-gray-700 bg-red-50 rounded-lg px-3 py-2">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {displayedResults.rewrite_suggestions?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Rewrite Suggestions</p>
            <ul className="space-y-2">
              {displayedResults.rewrite_suggestions.map((item, i) => (
                <li key={i} className="text-sm text-gray-700 bg-indigo-50 rounded-lg px-3 py-2">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* FIX #6: Enhance CV button now correctly inside ATSResults return */}
        <div className="pt-4 border-t">
          <Button
            onClick={handleEnhanceCV}
            disabled={enhancing || !approvedRecommendations.length}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {enhancing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Incorporating approved changes...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Incorporate {approvedRecommendations.length} approved change{approvedRecommendations.length === 1 ? '' : 's'}</>
            )}
          </Button>
          {!approvedRecommendations.length && <p className="text-xs text-center text-gray-500 mt-2">Approve one or more recommendations to continue.</p>}
        </div>

        {/* Enhanced CV Output */}
        {enhancedCV && (
          <>
            {enhancementWarning && (
              <div className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {enhancementWarning}
              </div>
            )}
            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
              <p className="font-semibold mb-1">✨ Complete enhanced CV</p>
              <p className="text-xs text-gray-500 mb-3">Review the full CV below before saving or downloading it.</p>
              <div className="max-h-[34rem] overflow-y-auto whitespace-pre-wrap rounded-md bg-gray-50 p-4 font-mono text-sm leading-6 text-gray-800">
                {enhancedCV}
              </div>
            </div>
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              className="w-full mt-2"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            {onReplaceWorkingCV && (
              <Button
                onClick={() => onReplaceWorkingCV(enhancedCV)}
                variant="outline"
                className="w-full mt-2 border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <Check className="w-4 h-4 mr-2" /> Use this as my working CV
              </Button>
            )}
          </>
        )}

      </CardContent>
    </Card>
  );
};


// ─── AI Suggestion Box ─────────────────────────────────────────────────────────
const AISuggestionBox = ({ suggestion }) => {
  if (!suggestion) return null;
  return (
    <div className="mt-3 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">
      <Check className="h-4 w-4 shrink-0" /> AI polish applied. You can edit the rewritten text further.
    </div>
  );
};


// ─── Main Component ────────────────────────────────────────────────────────────
const ResumeBuilder = () => {
  const [activeTab, setActiveTab] = useState('build');

  // FIX #1: rawCVText moved here (was incorrectly declared outside any component)
  const [rawCVText, setRawCVText] = useState('');

  // FIX #4: cvjdText declared here (was used but never declared anywhere)
  const [cvjdText, setCvjdText] = useState('');
  const [cvjdCvText, setCvjdCvText] = useState('');
  const [cvjdCvFileName, setCvjdCvFileName] = useState('');
  const [cvjdJdFileName, setCvjdJdFileName] = useState('');
  const [cvjdTargetRole, setCvjdTargetRole] = useState('');
  const [cvjdCustomRole, setCvjdCustomRole] = useState('');

  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '', location: '',
    title: '', summary: '', experience: '', education: '', skills: ''
  });
  const [role, setRole] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [suggestions, setSuggestions] = useState({});
  const [loadingSection, setLoadingSection] = useState(null);
  const [sectionError, setSectionError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [atsResults, setATSResults] = useState(null);
  const templatePreviewRef = useRef(null);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const useEnhancedCVAsWorkingCopy = (enhancedText) => {
    setRawCVText(enhancedText);
    setCvjdCvText(enhancedText);
    setCvjdCvFileName('Enhanced CV (working copy)');
    setSectionError(null);
  };

  const handleCVJDFile = async (file, type) => {
    if (!file) return;
    if (!isSupportedUploadFile(file)) {
      setSectionError('Please upload a PDF, DOCX, or TXT file.');
      return;
    }
    try {
      const { text } = await APIService.extractResumeText(file);
      if (type === 'cv') {
        setCvjdCvText(text);
        setCvjdCvFileName(file.name);
      } else {
        setCvjdText(text);
        setCvjdJdFileName(file.name);
      }
      setSectionError(null);
    } catch (err) {
      setSectionError(`Could not read file: ${err.message}`);
    }
  };

  const handleAISuggest = async (section) => {
    if (!role.trim()) {
      setSectionError('Please enter your target role first (top of form).');
      return;
    }
    if (!formData[section].trim()) {
      setSectionError(`Add or dictate your ${section} content before using AI Suggest.`);
      return;
    }
    setSectionError(null);
    setLoadingSection(section);
    try {
      const result = await APIService.generateResumeSuggestions(
        section,
        formData[section],
        role,
        parseInt(experienceYears) || 0
      );
      const improvedText = result.improved_text || result.suggestion || result.content;
      if (typeof improvedText !== 'string' || !improvedText.trim()) {
        throw new Error('AI did not return rewritten text. Please try again.');
      }
      handleChange(section, improvedText.trim());
      setSuggestions(prev => ({ ...prev, [section]: true }));
    } catch (err) {
      setSectionError(`AI suggestion failed: ${err.message}`);
    } finally {
      setLoadingSection(null);
    }
  };

  const handleAnalyzeATS = async () => {
    if (!role.trim()) { setSectionError('Please enter a target role to analyze ATS.'); return; }
    setSectionError(null);
    setLoadingSection('ats');
    try {
      const resumeText = `${formData.fullName}\n${formData.title}\n\nSummary:\n${formData.summary}\n\nExperience:\n${formData.experience}\n\nEducation:\n${formData.education}\n\nSkills:\n${formData.skills}`;
      const result = await APIService.analyzeATS(resumeText, role);
      setATSResults(result);
    } catch (err) {
      setSectionError(`ATS analysis failed: ${err.message}`);
    } finally {
      setLoadingSection(null);
    }
  };

  const handleDownloadResume = () => {
    if (!formData.fullName.trim() && !formData.summary.trim() && !formData.experience.trim()) {
      setSectionError('Add some resume details before downloading a template.');
      return;
    }
    const template = TEMPLATE_DETAILS[selectedTemplate];
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 16;
    const lineHeight = 5.5;
    let y = margin;
    const addLines = (text, fontSize = 10) => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, 178);
      lines.forEach((line) => {
        if (y + lineHeight > pageHeight - margin) { doc.addPage(); y = margin; }
        doc.text(line, margin, y);
        y += lineHeight;
      });
    };
    doc.setFillColor(...template.color);
    if (selectedTemplate === 'modern' || selectedTemplate === 'executive') doc.rect(0, 0, 210, 11, 'F');
    y = selectedTemplate === 'modern' || selectedTemplate === 'executive' ? 22 : margin;
    doc.setTextColor(...template.color);
    doc.setFont(selectedTemplate === 'classic' ? 'times' : 'helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(formData.fullName || 'Your Name', margin, y);
    y += 7;
    doc.setFontSize(11);
    doc.text(formData.title || role || 'Professional Title', margin, y);
    y += 7;
    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'normal');
    addLines([formData.email, formData.phone, formData.location].filter(Boolean).join(' | '), 9);
    const sections = [['PROFESSIONAL SUMMARY', formData.summary], ['EXPERIENCE', formData.experience], ['EDUCATION', formData.education], ['SKILLS', formData.skills]];
    sections.forEach(([heading, content]) => {
      if (!content.trim()) return;
      y += 4;
      if (y + 10 > pageHeight - margin) { doc.addPage(); y = margin; }
      doc.setTextColor(...template.color);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(heading, margin, y);
      y += 6;
      doc.setTextColor(55, 65, 81);
      doc.setFont('helvetica', 'normal');
      addLines(content, 10);
    });
    doc.save(`${(formData.fullName || 'Resume').replace(/[^a-z0-9]/gi, '_')}_${template.name}_Resume.pdf`);
  };

  const handlePreviewResume = () => templatePreviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // FIX #8: compareCVJD handler — ensure APIService.compareCVJD exists in api.js
  const handleCompareCVJD = async () => {
    const cvText = cvjdCvText.trim() ? cvjdCvText : rawCVText;
    const targetRole = cvjdTargetRole === CUSTOM_ROLE_VALUE ? cvjdCustomRole.trim() : cvjdTargetRole;
    if (!cvText.trim()) { setSectionError('Please upload a CV to compare.'); return; }
    if (!cvjdText.trim()) { setSectionError('Please paste a job description to compare.'); return; }
    if (!targetRole) { setSectionError('Please choose a target role or enter a custom role.'); return; }
    setSectionError(null);
    setLoadingSection('cvjd');
    try {
      const result = await APIService.compareCVJD(cvText, cvjdText, targetRole);
      setATSResults(result);
      setRawCVText(cvText);
      setRole(targetRole);
    } catch (err) {
      setSectionError(`CV vs JD comparison failed: ${err.message}`);
    } finally {
      setLoadingSection(null);
    }
  };

  const templates = [
    { id: 'modern', name: 'Modern', preview: 'Clean & professional' },
    { id: 'classic', name: 'Classic', preview: 'Traditional format' },
    { id: 'executive', name: 'Executive', preview: 'Leadership-focused' },
    { id: 'minimal', name: 'Minimal', preview: 'Simple & elegant' },
  ];

  const SectionAIButton = ({ section }) => (
    <Button
      variant="outline"
      size="sm"
      onClick={() => handleAISuggest(section)}
      disabled={loadingSection === section}
      className="flex items-center gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50"
    >
      {loadingSection === section
        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
        : <><Sparkles className="w-3.5 h-3.5" /> AI Suggest</>
      }
    </Button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-gray-100 pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" /> AI-Powered Resume Builder
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Build Your Perfect Resume</h1>
            <p className="text-lg text-gray-600">Create an ATS-ready resume in minutes — or upload yours for instant analysis</p>
          </div>

          {/* FIX #7: All three tab buttons now correctly inside the same tab switcher div */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
              <button
                onClick={() => setActiveTab('build')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === 'build' ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText className="w-4 h-4" /> Build from Scratch
              </button>
              <button
                onClick={() => setActiveTab('upload')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === 'upload' ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Upload className="w-4 h-4" /> Upload & Analyze
              </button>
              {/* FIX #7: CV vs JD tab button moved inside the switcher div */}
              <button
                onClick={() => setActiveTab('cvjd')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === 'cvjd' ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Briefcase className="w-4 h-4" /> CV vs JD
              </button>
            </div>
          </div>

          {/* Error Banner */}
          {sectionError && (
            <div className="flex items-center gap-2 p-3 mb-6 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {sectionError}
              <button onClick={() => setSectionError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* ── UPLOAD TAB ── */}
          {activeTab === 'upload' && (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Upload Your Resume</CardTitle>
                    <CardDescription>Get instant ATS score, keyword analysis, and AI recommendations</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <UploadTab
                      onAnalysisComplete={(result, text, targetRole) => {
                        setATSResults(result);
                        setRawCVText(text);
                        setRole(targetRole);
                      }}
                    />
                  </CardContent>
                </Card>
              </div>
              <div>
                {atsResults
                  ? <ATSResults results={atsResults} rawCVText={rawCVText} targetRole={role} onReplaceWorkingCV={useEnhancedCVAsWorkingCopy} />
                  : (
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
                      <CardContent className="pt-6 text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Sparkles className="w-8 h-8 text-blue-600" />
                        </div>
                        <p className="font-semibold text-gray-700 mb-2">AI Analysis Ready</p>
                        <p className="text-sm text-gray-500">Upload your resume and enter a target role to see your ATS score, matched keywords, and personalized improvement tips.</p>
                      </CardContent>
                    </Card>
                  )
                }
              </div>
            </div>
          )}

          {/* ── CV VS JD TAB ── */}
          {/* FIX #3 & #4: cvjdText tab now correctly placed at top-level alongside other tabs */}
          {activeTab === 'cvjd' && (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Compare CV vs Job Description</CardTitle>
                    <CardDescription>
                      Upload a CV and job description to see gaps, ATS score, and AI rewrite guidance.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <TargetRoleField id="cvjd-target-role" selectedRole={cvjdTargetRole} customRole={cvjdCustomRole} onSelectedRoleChange={setCvjdTargetRole} onCustomRoleChange={setCvjdCustomRole} />
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-gray-700 mb-1 block">
                          Upload CV <span className="text-red-500">*</span>
                        </Label>
                        <label className="flex min-h-[112px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white p-4 text-center hover:border-blue-300 hover:bg-blue-50">
                          <input
                            type="file"
                            accept=".pdf,.docx,.txt"
                            className="hidden"
                            onChange={(e) => handleCVJDFile(e.target.files[0], 'cv')}
                          />
                          <Upload className="w-6 h-6 text-blue-600 mb-2" />
                          <span className="text-sm font-semibold text-gray-700">
                            {cvjdCvFileName || (rawCVText ? 'Use previously uploaded CV' : 'Choose CV file')}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">PDF, DOCX, or TXT</span>
                        </label>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-gray-700 mb-1 block">
                          Upload JD
                        </Label>
                        <label className="flex min-h-[112px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white p-4 text-center hover:border-blue-300 hover:bg-blue-50">
                          <input
                            type="file"
                            accept=".pdf,.docx,.txt"
                            className="hidden"
                            onChange={(e) => handleCVJDFile(e.target.files[0], 'jd')}
                          />
                          <Briefcase className="w-6 h-6 text-blue-600 mb-2" />
                          <span className="text-sm font-semibold text-gray-700">
                            {cvjdJdFileName || 'Choose JD file'}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">Or paste the JD below</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-gray-700 mb-1 block">
                        Job Description <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        placeholder="Paste the full job description here, or upload a JD file above..."
                        rows={10}
                        value={cvjdText}
                        onChange={(e) => setCvjdText(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={handleCompareCVJD}
                      disabled={loadingSection === 'cvjd' || (!cvjdCvText && !rawCVText)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {loadingSection === 'cvjd' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing Match...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Analyze Match</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
              <div>
                {atsResults
                  ? <ATSResults results={atsResults} rawCVText={cvjdCvText || rawCVText} jdText={cvjdText} targetRole={role} onReplaceWorkingCV={useEnhancedCVAsWorkingCopy} />
                  : (
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
                      <CardContent className="pt-6 text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Briefcase className="w-8 h-8 text-blue-600" />
                        </div>
                        <p className="font-semibold text-gray-700 mb-2">Match Analysis Ready</p>
                        <p className="text-sm text-gray-500">Upload your CV, add the job description, and compare alignment against the role.</p>
                      </CardContent>
                    </Card>
                  )
                }
              </div>
            </div>
          )}

          {/* ── BUILD TAB ── */}
          {activeTab === 'build' && (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">

                {/* Target Role Banner */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-5">
                    <p className="text-sm font-semibold text-blue-800 mb-3">🎯 Set your target role — AI suggestions will be tailored to this</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <div className="mb-1 flex items-center justify-between">
                          <Label className="text-xs text-blue-700 font-semibold">Target Role *</Label>
                          <VoiceInputButton value={role} onTranscript={setRole} fieldName="target role" className="h-7 text-xs" />
                        </div>
                        <Input placeholder="e.g. VP Finance, CA, CFO" value={role} onChange={(e) => setRole(e.target.value)} className="bg-white" />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between">
                          <Label className="text-xs text-blue-700 font-semibold">Years of Experience</Label>
                          <VoiceInputButton value={experienceYears} onTranscript={setExperienceYears} fieldName="years of experience" className="h-7 text-xs" />
                        </div>
                        <Input type="number" placeholder="e.g. 20" value={experienceYears} onChange={(e) => setExperienceYears(e.target.value)} className="bg-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Personal Info */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      <div>
                        <CardTitle>Personal Information</CardTitle>
                        <CardDescription>Your basic contact details</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="mb-1 flex items-center justify-between"><Label htmlFor="fullName">Full Name</Label><VoiceInputButton value={formData.fullName} onTranscript={(value) => handleChange('fullName', value)} fieldName="full name" className="h-7 text-xs" /></div>
                        <Input id="fullName" placeholder="Rupinder Singh" value={formData.fullName} onChange={(e) => handleChange('fullName', e.target.value)} />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between"><Label htmlFor="title">Professional Title</Label><VoiceInputButton value={formData.title} onTranscript={(value) => handleChange('title', value)} fieldName="professional title" className="h-7 text-xs" /></div>
                        <Input id="title" placeholder="Chartered Accountant | VP Audit" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="mb-1 flex items-center justify-between"><Label htmlFor="email">Email</Label><VoiceInputButton value={formData.email} onTranscript={(value) => handleChange('email', value)} fieldName="email" className="h-7 text-xs" /></div>
                        <Input id="email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between"><Label htmlFor="phone">Phone</Label><VoiceInputButton value={formData.phone} onTranscript={(value) => handleChange('phone', value)} fieldName="phone number" className="h-7 text-xs" /></div>
                        <Input id="phone" placeholder="+91 98765 43210" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 flex items-center justify-between"><Label htmlFor="location">Location</Label><VoiceInputButton value={formData.location} onTranscript={(value) => handleChange('location', value)} fieldName="location" className="h-7 text-xs" /></div>
                      <Input id="location" placeholder="New Delhi, India" value={formData.location} onChange={(e) => handleChange('location', e.target.value)} />
                    </div>
                  </CardContent>
                </Card>

                {/* Summary */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                        <div>
                          <CardTitle>Professional Summary</CardTitle>
                          <CardDescription>Compelling overview of your career</CardDescription>
                        </div>
                      </div>
                      <div className="flex gap-2"><VoiceInputButton value={formData.summary} onTranscript={(value) => handleChange('summary', value)} fieldName="professional summary" /><SectionAIButton section="summary" /></div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder="Brief overview of your professional experience and key achievements..."
                      rows={4}
                      value={formData.summary}
                      onChange={(e) => handleChange('summary', e.target.value)}
                    />
                    <AISuggestionBox
                      suggestion={suggestions.summary}
                    />
                  </CardContent>
                </Card>

                {/* Experience */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                        <div>
                          <CardTitle>Work Experience</CardTitle>
                          <CardDescription>Your professional history and achievements</CardDescription>
                        </div>
                      </div>
                      <div className="flex gap-2"><VoiceInputButton value={formData.experience} onTranscript={(value) => handleChange('experience', value)} fieldName="work experience" /><SectionAIButton section="experience" /></div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder="Company | Role | Duration&#10;• Key achievement with quantifiable result&#10;• Led a team of X to deliver Y..."
                      rows={7}
                      value={formData.experience}
                      onChange={(e) => handleChange('experience', e.target.value)}
                    />
                    <AISuggestionBox
                      suggestion={suggestions.experience}
                    />
                  </CardContent>
                </Card>

                {/* Education */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                        <div>
                          <CardTitle>Education</CardTitle>
                          <CardDescription>Degrees and certifications</CardDescription>
                        </div>
                      </div>
                      <div className="flex gap-2"><VoiceInputButton value={formData.education} onTranscript={(value) => handleChange('education', value)} fieldName="education" /><SectionAIButton section="education" /></div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder="CA | ICAI | 2004&#10;B.Com | Delhi University | 2002"
                      rows={3}
                      value={formData.education}
                      onChange={(e) => handleChange('education', e.target.value)}
                    />
                    <AISuggestionBox
                      suggestion={suggestions.education}
                    />
                  </CardContent>
                </Card>

                {/* Skills */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-blue-600" />
                        <div>
                          <CardTitle>Skills</CardTitle>
                          <CardDescription>Technical and soft skills</CardDescription>
                        </div>
                      </div>
                      <div className="flex gap-2"><VoiceInputButton value={formData.skills} onTranscript={(value) => handleChange('skills', value)} fieldName="skills" /><SectionAIButton section="skills" /></div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder="Financial Reporting, Audit, Risk Management, IFRS, SAP..."
                      rows={3}
                      value={formData.skills}
                      onChange={(e) => handleChange('skills', e.target.value)}
                    />
                    <AISuggestionBox
                      suggestion={suggestions.skills}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">

                {/* Template Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Choose Template</CardTitle>
                    <CardDescription>Choose a design to update the live preview and downloaded PDF.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {templates.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => setSelectedTemplate(t.id)}
                          className={`overflow-hidden rounded-xl border-2 text-left transition-all ${
                            selectedTemplate === t.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-200'
                          }`}
                        >
                          <ResumeTemplatePreview template={t.id} formData={formData} role={role} compact />
                          <div className="p-2"><div className="font-semibold text-sm">{t.name}</div><div className="text-xs text-gray-500 mt-0.5">{t.preview}</div></div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card ref={templatePreviewRef} className="overflow-hidden">
                  <CardHeader>
                    <CardTitle className="text-base">Live resume preview</CardTitle>
                    <CardDescription>{TEMPLATE_DETAILS[selectedTemplate].name} template</CardDescription>
                  </CardHeader>
                  <CardContent className="bg-gray-100 p-3">
                    <ResumeTemplatePreview template={selectedTemplate} formData={formData} role={role} />
                  </CardContent>
                </Card>

                {/* ATS Score / Analyze Button */}
                <Card className={`${atsResults ? 'border-green-200 bg-gradient-to-br from-green-50 to-blue-50' : 'bg-gradient-to-br from-slate-50 to-blue-50'}`}>
                  <CardHeader>
                    <CardTitle className="text-base">ATS Compatibility</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {atsResults ? (
                      <ATSResults results={atsResults} rawCVText={rawCVText} targetRole={role} onReplaceWorkingCV={useEnhancedCVAsWorkingCopy} />
                    ) : (
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-4">Check how well your resume matches your target role's ATS requirements</p>
                        <Button
                          onClick={handleAnalyzeATS}
                          disabled={loadingSection === 'ats'}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          size="sm"
                        >
                          {loadingSection === 'ats'
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                            : <><Sparkles className="w-4 h-4 mr-2" /> Analyze ATS Score</>
                          }
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Actions */}
                <Card>
                  <CardContent className="pt-6 space-y-2">
                    <Button onClick={handlePreviewResume} className="w-full h-11 bg-blue-600 hover:bg-blue-700 font-semibold">
                      <FileText className="w-4 h-4 mr-2" /> Preview Resume
                    </Button>
                    <Button onClick={handleDownloadResume} variant="outline" className="w-full h-11 font-semibold">
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                  </CardContent>
                </Card>

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ResumeBuilder;
